<template>
  <div>
    <h2 class="mb-5">{{ defaultHeader }}</h2>

    <ServerErrorModal v-if="errors.show" :error="errors.error" @closeModal="errors.show = false" />
    <!-- Credit Note Modal -->
    <CreditNoteModal
      v-if="isCreditNoteModalShown"
      class="credit-note-modal"
      :landlordId="landlordId"
      :tenancyId="tenancyId"
      :orderId="invoiceNumberToStartAnAction"
      @newCreditNote="fetchLedger"
    />
    <!-- / Credit Note Modal -->

    <!-- Cancel Direct Payment Modal -->
    <hl-Modal
      v-if="showCancelDirectPaymentModal"
      class="text-center cancel-direct-payment-modal"
      iconClass="budicon-trash text-danger"
      title="Er du sikker på at du vil annulere direktebetaling?"
    >
      <div>
        <button class="btn btn-danger mx-2" @click="cancelDirectPayment">Bekreft</button>
        <button class="btn btn-outline-secondary mx-2" @click="showCancelDirectPaymentModal = false">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- Cancel Direct Payment Modal -->

    <!-- Unlink Invoice From Invoice Modal -->
    <hl-Modal
      v-if="showUnLinkInvoiceFromInvoiceModal"
      class="text-center cancel-direct-payment-modal"
      iconClass="budicon-trash text-danger"
      title="Er du sikker på at du vil brytte faktura kobling?"
    >
      <div>
        <button class="btn btn-danger mx-2" @click="disconnectInvoice">Bekreft</button>
        <button class="btn btn-outline-secondary mx-2" @click="showUnLinkInvoiceFromInvoiceModal = false">
          Avbryt
        </button>
      </div>
    </hl-Modal>
    <!-- Unlink Invoice From Invoice Modal -->

    <!-- Connect To Invoice Modal -->
    <ConnectPaymentToOtherInvoiceModal
      v-if="showConnectPaymentToOtherInvoiceModal"
      :landlordId="landlordId"
      :tenancyId="tenancyId"
      :sourceOrder="connectSourceOrder"
      @closedConnectPaymentToOtherInvoiceModal="refetchLedgerAndCloseConnectPaymentToOtherInvoiceModal"
    ></ConnectPaymentToOtherInvoiceModal>
    <!-- / Connect To Invoice Modal -->

    <!-- Connect To Invoice Modal -->
    <ConnectPaymentToOtherPaymentModal
      v-if="showConnectPaymentToOtherPaymentModal"
      :landlordId="landlordId"
      :tenancyId="tenancyId"
      :sourceOrder="connectSourceOrder"
      @closedConnectPaymentToOtherPaymentModal="refetchLedgerAndShowConnectPaymentToOtherPaymentModal"
    ></ConnectPaymentToOtherPaymentModal>
    <!-- / Connect To Invoice Modal -->

    <div class="mb-4">
      <router-link
        class="ledger-breadcrumb h6"
        :to="{ name: 'tenancy_payments', params: { rentalObjectId, tenancyId } }"
      >
        <span>
          <i class="budicon-arrow-left mr-2"></i>
          <span>Betalinger</span>
        </span>
      </router-link>
    </div>

    <BlockContainer>
      <!-- Loader -->
      <div v-if="loading.ledger" class="text-center py-5">
        <div class="spinner-grow text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <!-- / Loader -->

      <div v-if="ledger">
        <div class="ledger-header">
          <div class="px-3 px-md-5">
            <h3>Reskontro - {{ tenantName }}, {{ rentalObjectAddress }}</h3>
          </div>
        </div>

        <div class="mt-0 p-3 p-md-5">
          <div class="mt-2 mb-5 row">
            <div class="col">
              <hl-Select label="Status" v-model="currentBalance" :options="ledgerStatusOptions"></hl-Select>
            </div>

            <div class="col">
              <hl-Select label="År" :options="ledgerYearsOptions" v-model="selectedYear"></hl-Select>
            </div>
          </div>

          <div class="table-responsive">
            <table class="ledger-table table">
              <thead>
                <tr>
                  <th scope="col">Faktura</th>
                  <th scope="col">Dato</th>
                  <th scope="col">Type</th>
                  <th scope="col" class="text-right">Beløp</th>
                  <th scope="col">Handlinger</th>
                </tr>
              </thead>

              <tbody v-for="(post, i) in filteredLedger" :key="i">
                <tr v-for="row in post.rows" :key="row.id">
                  <td scope="row">{{ row.invoiceNumber }}</td>
                  <td>
                    <hl-Date :date="row.date" />
                  </td>
                  <td :class="{ 'line-through': row.isCorrection }">{{ eventTypeMap[row.type] }}</td>
                  <td class="text-right" :class="{ 'line-through': row.isCorrection }">
                    <hl-Currency hideSymbol :amount="row.amount" :class="getAmountClass(row.amount)"></hl-Currency>
                  </td>
                  <td>
                    <ActionsMenu
                      v-if="shouldShowDropdown(row) || isUserSudo"
                      :menuItems="actionMenuItems(row)"
                      :class="{ sudo: !shouldShowDropdown(row) && isUserSudo }"
                      variant="button"
                      buttonText="Handlinger"
                    />
                  </td>
                </tr>
                <tr class="saldo">
                  <td></td>
                  <td></td>
                  <th>Saldo</th>
                  <th class="text-right">
                    <hl-Currency hideSymbol :amount="post.balance" :class="getAmountClass(post.balance)"></hl-Currency>
                  </th>
                  <td></td>
                </tr>
              </tbody>
              <tbody>
                <tr>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                  <td>&nbsp;</td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <th>Total fakturert</th>
                  <th class="text-right">
                    <hl-Currency
                      hideSymbol
                      :amount="ledger.currentBalance[currentBalance].invoiced"
                      :class="getAmountClass(ledger.currentBalance[currentBalance].invoiced)"
                    ></hl-Currency>
                  </th>
                  <td></td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <th>Total innbetalt</th>
                  <th class="text-right">
                    <hl-Currency
                      hideSymbol
                      :amount="ledger.currentBalance[currentBalance].paid"
                      :class="getAmountClass(ledger.currentBalance[currentBalance].paid)"
                    ></hl-Currency>
                  </th>
                  <td></td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <th>Total kreditert</th>
                  <th class="text-right">
                    <hl-Currency
                      hideSymbol
                      :amount="ledger.currentBalance[currentBalance].creditNote"
                      :class="getAmountClass(ledger.currentBalance[currentBalance].creditNote)"
                    ></hl-Currency>
                  </th>
                  <td></td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <th>Saldo</th>
                  <th class="text-right">
                    <hl-Currency hideSymbol :amount="totalBalance" :class="getAmountClass(totalBalance)"></hl-Currency>
                  </th>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-5">
            <button class="btn btn-sm btn-outline-secondary mr-2" @click="downloadCSV">CSV av skjermdata</button>
            <!--<button class="btn btn-sm btn-white ml-2">Last ned XLSX-fil</button>-->
          </div>
        </div>
      </div>
    </BlockContainer>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import CSVDoc from '../../lib/CSVDoc';
import BlockContainer from '@/components/BlockContainer';
import ConnectPaymentToOtherInvoiceModal from '@/components/tenancy/modals/ConnectPaymentToOtherInvoiceModal';
import ConnectPaymentToOtherPaymentModal from '@/components/tenancy/modals/ConnectPaymentToOtherPaymentModal';
import CreditNoteModal from '@/components/tenancy/modals/CreditNoteModal';
import ServerErrorModal from '@/components/UI/errors/ServerErrorModal';
import * as moment from 'moment';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import ActionsMenu from '@/components/UI/ActionsMenu';

export default {
  components: {
    BlockContainer,
    ConnectPaymentToOtherInvoiceModal,
    ConnectPaymentToOtherPaymentModal,
    CreditNoteModal,
    ServerErrorModal,
    ActionsMenu,
    'hl-Modal': Modal,
    'hl-Currency': Currency,
    'hl-Date': Date,
    'hl-Select': Select,
  },

  props: ['rentalObjectId', 'landlordId', 'tenancyId', 'defaultHeader'],

  data() {
    return {
      activeTab: 'trail',
      currentBalance: 'open',
      ledger: null,
      selectedYear: 'all',
      eventTypeMap: {
        payment: 'Betaling',
        creditNote: 'Kreditnota',
        directPayment: 'Direktebetaling',
        invoice: 'Faktura',
      },
      loading: {
        ledger: false,
      },
      errors: {
        show: false,
        error: null,
      },
      invoiceNumberToStartAnAction: null,
      showCreditNoteModal: false,
      showConnectPaymentToOtherInvoiceModal: false,
      showConnectPaymentToOtherPaymentModal: false,
      showCancelDirectPaymentModal: false,
      showUnLinkInvoiceFromInvoiceModal: false,
      directPaymentToCancel: null,
      connectSourceOrder: null,
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancy', 'isCreditNoteModalShown', 'getTenancy']),
    ...mapGetters(['getRentalObject', 'getLandlordProfileMeta', 'isUserSudo']),

    tenantName() {
      return this.getTenancy.tenants.find(tenant => tenant.type === 'tenant').fullName;
    },

    rentalObjectAddress() {
      if (this.getTenancy == null) return;
      return this.getRentalObject(this.getTenancy.rentalObjectId)?.address.street;
    },

    customerId() {
      if (this.getTenancy == null) return;
      return this.getTenancy.details.customClientId;
    },

    buildingId() {
      if (this.getTenancy == null) return;
      return this.getRentalObject(this.getTenancy.rentalObjectId).details.buildingId;
    },

    apartmentNumber() {
      if (this.getTenancy == null) return;
      return this.getRentalObject(this.getTenancy.rentalObjectId).details.apartmentNumber;
    },

    roomNumber() {
      if (this.getTenancy == null) return;
      return this.getRentalObject(this.getTenancy.rentalObjectId).details.roomNumber;
    },

    rentalObjectName() {
      if (this.getTenancy == null) return;
      return this.getRentalObject(this.getTenancy.rentalObjectId).details.name;
    },

    landlordName() {
      try {
        return this.getLandlordProfileMeta(this.landlordId).fullName;
      } catch (error) {
        return null;
      }
    },

    ledgerStatusOptions() {
      return [
        { name: 'Alle', value: 'total' },
        { name: 'Åpent', value: 'open' },
        { name: 'Lukket', value: 'closed' },
      ];
    },

    ledgerYearsOptions() {
      const options = [
        {
          name: 'Alle',
          value: 'all',
        },
      ];

      if (this.ledger.years) {
        for (const year of this.ledger.years) {
          options.push({ name: year.toString(), value: year.toString() });
        }
        return options;
      }

      // fallback to this year
      const thisYear = moment().format('YYYY');
      return [{ name: thisYear, value: thisYear }];
    },

    filteredLedger() {
      if (this.ledger == null) return;
      // if no status is specified, or total is the filter, return the ledger unfiltered
      const posts = [...this.ledger.posts];
      posts.sort((a, b) => {
        try {
          const aInvoice = a.rows.find(x => x.type === 'invoice');
          const bInvoice = b.rows.find(x => x.type === 'invoice');
          return bInvoice.date.localeCompare(aInvoice.date);
        } catch (error) {
          // Without enough info we will just say these two are equal.
          return 0;
        }
      });
      if (this.currentBalance == null || this.currentBalance === 'total') {
        return posts;
      }
      return posts.filter(post => post.status === this.currentBalance);
    },

    totalBalance() {
      const current = this.ledger.currentBalance[this.currentBalance];
      return current.invoiced - current.creditNote - current.paid;
    },
  },

  methods: {
    ...mapMutations('tenancy', ['toggleCreditNoteModal']),
    ...mapActions('tenancy', [
      'fetchTenancyLedger',
      'deleteTenancyAccountingOrderDirectPayment',
      'deleteTenancyAccountingOrdersLink',
    ]),

    async fetchLedger() {
      this.loading.ledger = true;
      try {
        this.ledger = await this.fetchTenancyLedger({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data:
            this.selectedYear === 'all'
              ? {}
              : {
                  query: {
                    year: this.selectedYear,
                  },
                },
        });
        this.loading.ledger = false;
      } catch (error) {
        console.error(error);
        this.errors.error = error;
        this.errors.show = true;
        this.loading.ledger = false;
      }
    },

    toCSVDate(date) {
      const parts = date.split('-');
      const year = parts[0];
      // Strip leading zeros
      const month = String(parseInt(parts[1], 10));
      const day = String(parseInt(parts[2], 10));
      return `${month}/${day}/${year}`;
    },

    downloadCSV() {
      const data = this.filteredLedger;
      const doc = new CSVDoc({ columns: 4 })
        .addRow('Utleier', this.landlordName)
        .addRow('Utskriftsdato', moment().format('DD.MM.YYYY'))
        .addRow('Leietaker', this.tenantName)
        .addRow('KundeID', this.customerId)
        .addRow()
        .addRow('Leieobjekt', this.rentalObjectName)
        .addRow('ByggID', this.buildingId)
        .addRow('Leilighetsnummer', this.apartmentNumber)
        .addRow('Romnummer', this.roomNumber)
        .addRow()
        .addRow('Faktura#', 'Dato', 'Type', 'Beløp')
        .addRow();
      data.forEach(post => {
        post.rows.forEach(row => {
          doc.addRow(row.invoiceNumber, this.toCSVDate(row.date), this.eventTypeMap[row.type], row.amount);
        });
        doc.addRow(null, null, 'Saldo', post.balance).addRow();
      });
      const current = this.ledger.currentBalance[this.currentBalance];
      doc
        .addRow(null, null, 'Totalt fakturert', current.invoiced)
        .addRow(null, null, 'Totalt innbetalt', current.paid)
        .addRow(null, null, 'Totalt kreditert', current.creditNote)
        .addRow(null, null, 'Saldo', this.totalBalance)
        .download(moment().format('DD.MM.YYYY') + ' - ' + this.tenantName + ' - reskontro.csv');
    },

    getAmountClass(a) {
      if (a < 0) {
        return 'text-danger';
      }
      return 'text-black';
    },

    // actions
    openCreditNote(orderId) {
      this.invoiceNumberToStartAnAction = orderId;
      this.toggleCreditNoteModal(true);
    },

    emitOpenDirectPayment(orderId) {
      /* alert('lksjdlkj')
      this.$emit("openDirectPayment", orderId); */
      this.$router.push({
        name: 'tenancy_payments',
        params: {
          rentalObjectId: this.rentalObjectId,
          tenancyId: this.tenancyId,
        },
        query: { orderDetails: orderId, firstPage: 'actions' },
      });
    },

    openConnectPaymentToOtherInvoiceModal(order) {
      this.connectSourceOrder = order;
      this.showConnectPaymentToOtherInvoiceModal = true;
    },

    openConnectPaymentToOtherPaymentModal(order) {
      this.connectSourceOrder = order;
      this.showConnectPaymentToOtherPaymentModal = true;
    },

    openCancelDirectPaymentModal(order) {
      this.directPaymentToCancel = order;
      this.showCancelDirectPaymentModal = true;
    },

    openUnLinkInvoiceFromInvoiceModal(order) {
      this.invoiceToUnLink = order;
      this.showUnLinkInvoiceFromInvoiceModal = true;
    },

    async cancelDirectPayment() {
      try {
        await this.deleteTenancyAccountingOrderDirectPayment({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.directPaymentToCancel.orderId,
          paymentId: this.directPaymentToCancel.id,
        });
        this.$toast(
          `Direktebetaling på NOK ${this.directPaymentToCancel.amount} den ${this.directPaymentToCancel.date} har blitt annullert.`,
          'success'
        );
        this.showCancelDirectPaymentModal = false;
        await this.fetchLedger();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke avbryte betaling.', 'error');
      }
    },

    async disconnectInvoice() {
      try {
        await this.deleteTenancyAccountingOrdersLink({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.invoiceToUnLink.orderId,
        });
        this.showUnLinkInvoiceFromInvoiceModal = false;
        this.fetchLedger();
        this.$toast('Success', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke bryte faktura.', 'error');
      }
    },

    refetchLedgerAndCloseConnectPaymentToOtherInvoiceModal() {
      this.fetchLedger();
      this.showConnectPaymentToOtherInvoiceModal = false;
    },

    refetchLedgerAndShowConnectPaymentToOtherPaymentModal() {
      this.fetchLedger();
      this.showConnectPaymentToOtherPaymentModal = false;
    },

    shouldShowDropdown(row) {
      return (
        Object.keys(row.actions).length > 0 &&
        !row.isCorrection &&
        this.getTenancy?.subscription?.subscriptionPlanName !== 'Garantert'
      );
    },

    actionMenuItems(row) {
      return [
        row.actions.creditNote
          ? {
              name: 'Opprett kreditnota',
              action: () => this.openCreditNote(row.orderId),
            }
          : undefined,
        row.actions.directPayment
          ? {
              name: 'Registrer direktebetaling',
              action: () => this.emitOpenDirectPayment(row.orderId),
            }
          : undefined,
        row.actions.link
          ? {
              name: 'Koble mot annen faktura',
              action: () => this.openConnectPaymentToOtherInvoiceModal(row),
            }
          : undefined,
        row.actions.unLink
          ? {
              name: 'Bryt faktura kobling',
              action: () => this.openUnLinkInvoiceFromInvoiceModal(row),
            }
          : undefined,
        row.actions.movePayment
          ? {
              name: 'Koble om betaling',
              action: () => this.openConnectPaymentToOtherPaymentModal(row),
            }
          : undefined,
        row.actions.voidDirectPayment
          ? {
              name: 'Annuler direktebetaling',
              action: () => this.openCancelDirectPaymentModal(row),
            }
          : undefined,
      ].filter(item => item);
    },
  },

  mounted() {
    this.fetchLedger();
  },

  watch: {
    selectedYear() {
      this.fetchLedger();
    },
  },
};
</script>

<style scoped lang="scss">
.ledger-header {
  box-shadow: $box-shadow-2;
  padding: 2rem 0;
}

.ledger-breadcrumb {
  color: inherit;
  text-decoration: none;
}

.line-through {
  text-decoration: line-through;
}

.credit-note-modal,
.cancel-direct-payment-modal {
  z-index: 1999;
}

.saldo {
  border-top: 2px solid #dee2e6;
}
.sudo {
  border: 2px solid #ed2135;
  border-radius: $border-radius;
}
</style>
